/**
 * ------------------------------------------------------------------------
 * Colour Swatches  (assets/color-swatches.js)  -  PDP version
 * ------------------------------------------------------------------------
 * Powers the <color-swatches> custom element rendered by
 * blocks/color-swatches.liquid.
 *
 * On click, instead of a normal full-page navigation to the other
 * colourway's URL, this:
 *
 *   1. Fetches that same URL with `?section_id={sectionId}` appended -
 *      Shopify's built-in Section Rendering API, which returns just this
 *      one section re-rendered for that product (fresh media gallery,
 *      price, variant picker, swatch row - everything).
 *   2. Swaps the current `#shopify-section-{sectionId}` element's
 *      outerHTML for that response, so the page updates in place.
 *   3. Pushes the new URL via history.pushState so the address bar,
 *      back button, and page are all correct/shareable/bookmarkable.
 *
 * If anything about that fails - network error, unexpected response,
 * missing section markup, etc. - it falls back to a normal link
 * navigation (`window.location.href = url`), so a swatch click can never
 * leave the user stuck on a broken/half-updated page.
 */

class ColorSwatches extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const swatch = event.target.closest('[data-color-swatch]');
    if (!swatch) return;

    // Already viewing this colourway - let it act as a no-op link.
    if (swatch.hasAttribute('aria-current')) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.swapTo(swatch.href);
  }

  async swapTo(url) {
    if (!this.sectionId) {
      window.location.href = url;
      return;
    }

    this.setAttribute('aria-busy', 'true');

    try {
      const sectionUrl = this.buildSectionUrl(url, this.sectionId);
      const response = await fetch(sectionUrl);
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

      const html = await response.text();
      const newSection = this.extractSection(html, this.sectionId);
      const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);

      if (!newSection || !currentSection) {
        throw new Error('Could not find rendered section markup.');
      }

      currentSection.outerHTML = newSection;
      window.history.pushState({ colorSwatchUrl: url }, '', url);
      window.dispatchEvent(new CustomEvent('color-swatches:swapped', { detail: { url } }));
    } catch (error) {
      // Any failure at all - just navigate normally. Never leave the
      // shopper stuck on a half-updated page.
      window.location.href = url;
    }
  }

  buildSectionUrl(url, sectionId) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  }

  extractSection(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.getElementById(`shopify-section-${sectionId}`);
    if (section) return section.outerHTML;

    // Some section-rendering responses return only the section's inner
    // content (no wrapping #shopify-section-{id} div) rather than the
    // full wrapper. In that case, rebuild the wrapper ourselves so the
    // outerHTML swap target still matches.
    const trimmed = html.trim();
    if (trimmed) {
      return `<div id="shopify-section-${sectionId}" class="shopify-section">${trimmed}</div>`;
    }
    return null;
  }
}

if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}

// Keep the browser back/forward buttons working after an in-place swap:
// a popstate to a URL we swapped to should re-swap (or, simplest and most
// robust, just do a normal navigation - the browser already has the new
// URL, so a reload is a correct, if slightly heavier, way to sync the DOM).
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.colorSwatchUrl) {
    window.location.reload();
  }
});